import React from 'react'
import { useTranslation } from 'react-i18next'

import { isSameDay } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'

import { AvailabilityStrip, StripDay } from './AvailabilityStrip'
import * as styles from './WeeklyAvailabilityCalendar.module.scss'

import { TIMEZONE, VILLA_BRAMARE_SLUG } from '../../../constants'
import {
  getAvailability,
  useGameDays,
  usePropertyDetailAvailabilitiesApi,
} from '../../../hooks'
import { addJstDate, getDefaultCheckInDate, getNights } from '../../../utils'
import { CalendarModal } from '../../modals'

const WEEK = 7
// CI(チェックイン)日を週(7日)の中央に置くためのオフセット。CI を 4 日目に
// 来させる = 表示開始日は CI の 3 日前。
const CENTER_OFFSET = 3

type Props = {
  property?: string
  checkInDate: string
  // 元の検索のチェックアウト日。日付セルから日付変更モーダルを開くとき、
  // 元の宿泊数を維持してチェックアウトを算出するために使う（無ければ1泊）。
  checkOutDate?: string
  propertyName?: string
  onSearch: (checkInDate: string, checkOutDate: string) => void
}

const formatMD = (date: string) => {
  const [, m, d] = date.split('-').map(Number)
  return `${m}/${d}`
}

export const WeeklyAvailabilityCalendar: React.FC<Props> = ({
  property,
  checkInDate,
  checkOutDate,
  propertyName = '',
  onSearch,
}) => {
  const { t } = useTranslation()
  const [weekOffset, setWeekOffset] = React.useState(0)
  const [modalDate, setModalDate] = React.useState<string | null>(null)
  const { isGameDay, gameDays } = useGameDays()

  // checkInDate が外から（サイドバー再検索など、モーダル外）変わったら週オフセットを
  // 0 に戻す。残ったままだと fromDate = 新CI + 旧offset*7 と、選択日からずれた週を
  // 表示・取得してしまう。
  React.useEffect(() => {
    setWeekOffset(0)
  }, [checkInDate])

  const today = getDefaultCheckInDate()

  // CI を週の中央(4日目)に置く。ただし CI-3 が過去日に食い込む場合は today 始まりに
  // クランプし、無意味な過去日を見せない（このとき CI は実質左端になる）。
  const centeredStart = addJstDate(checkInDate, weekOffset * WEEK - CENTER_OFFSET)
  const fromDate = centeredStart < today ? today : centeredStart
  const toDate = addJstDate(fromDate, WEEK - 1)

  // 元の検索の宿泊数を維持する。日付セルをクリックして開くモーダルでは、
  // 「クリック日 + 元の宿泊数」をチェックアウト初期値にする。元のチェックアウトが
  // 無い/不正なら最低1泊にフォールバック（3泊→1泊に縮める誤りを防ぐ）。
  const nights = Math.max(getNights(checkInDate, checkOutDate) ?? 1, 1)

  // 前週へ移動できるのは、クランプ後の前週開始日が現在の開始日より前にある場合だけ
  // （= today より前には戻れない）。
  const prevCentered = addJstDate(
    checkInDate,
    (weekOffset - 1) * WEEK - CENTER_OFFSET
  )
  const prevFromDate = prevCentered < today ? today : prevCentered
  const canGoPrev = prevFromDate < fromDate

  const { data, error, isLoading } = usePropertyDetailAvailabilitiesApi(
    property,
    fromDate,
    toDate
  )

  const days: StripDay[] =
    !error && data?.availabilities
      ? data.availabilities
          .filter((a): a is typeof a & { date: string } => a.date != null)
          .map((a) => {
            const zoned = utcToZonedTime(a.date, TIMEZONE)
            const isGame = isGameDay(zoned)
            const gameInfo = isGame
              ? gameDays.find((g) => isSameDay(g.date, zoned))
              : undefined
            // ヴィラ(villa_bramare)は在庫率ではなく在庫数で判定する。
            // FullCalendar と同じ分岐に揃え、◯△× がカレンダーと食い違わないようにする。
            const availability =
              property === VILLA_BRAMARE_SLUG
                ? getAvailability({ count: a.availableCount ?? null })
                : getAvailability({ rate: a.availableRate ?? null })
            return {
              date: a.date,
              availability,
              isCheckIn: a.date === checkInDate,
              isGameDay: isGame,
              gameImage: gameInfo?.image,
            }
          })
      : []

  // SWR の再取得中は data が一旦 undefined になり days=[] でストリップが
  // 空になる。週移動(◀/▶)でのブランク点滅を避けるため、直近の非空 days を
  // ref に保持し、読み込み中に現在 days が空なら前回分を表示し続ける。
  const lastDaysRef = React.useRef<StripDay[]>([])
  if (days.length > 0) lastDaysRef.current = days
  const isStale = days.length === 0 && isLoading
  const shownDays = isStale ? lastDaysRef.current : days

  return (
    <div className={styles.wrapper}>
      <div className={styles.nav}>
        <button
          type="button"
          className={
            canGoPrev ? styles.prev : `${styles.prev} ${styles.disabled}`
          }
          disabled={!canGoPrev}
          onClick={() => canGoPrev && setWeekOffset((w) => w - 1)}
        >
          {t('components.ScheduleTable.previousWeek')}
        </button>
        <span className={styles.range}>
          {t('empty.soldOutRecommend.weekRange', {
            from: formatMD(fromDate),
            to: formatMD(toDate),
          })}
        </span>
        <button
          type="button"
          className={styles.next}
          onClick={() => setWeekOffset((w) => w + 1)}
        >
          {t('components.ScheduleTable.nextWeek')}
        </button>
      </div>

      {/* 読み込み中に前週分(stale)を表示している間は、範囲ラベルは新しい週を指して
          いるのにセルは古い週のまま。古い日付でモーダルが開く事故を防ぐため、stale の
          間はクリックを無効化する（onDayClick を渡さない）。 */}
      <AvailabilityStrip
        days={shownDays}
        onDayClick={isStale ? undefined : setModalDate}
      />

      {modalDate && (
        <CalendarModal
          isOpen
          checkInParam={modalDate}
          checkOutParam={addJstDate(modalDate, nights)}
          propertyName={propertyName}
          mode="range"
          onClose={() => setModalDate(null)}
          onSearch={(ci, co) => {
            // 新しいチェックイン日が選ばれたら週オフセットを戻し、選択日を含む週を
            // 表示する（offset を残すと checkInDate からさらに weekOffset 週ぶん
            // ずれた週が出てしまう）。
            setWeekOffset(0)
            onSearch(ci, co)
            setModalDate(null)
          }}
        />
      )}
    </div>
  )
}
